// Commission reconciliation (idea 32): an insurer's commission statement (CSV / xlsx, columns mapped by the advisor:
// policy number, amount, period) compared with the commission the CRM expects (commission_entries, built from the
// production ledger by the commission rules). The real problem: statements arrive monthly per insurer, and missing or
// short payments are only found by hand, line by line.
//
// Result, per policy and period: matched (same amount), different (amount differs), missing (expected, not on the
// statement) and unexpected (on the statement, but no policy with that number, or no expected commission for it);
// unreadable rows are listed separately. NOTHING changes by comparing. The advisor may accept a statement line: that
// records it through acceptStatementLines (the expected lines are marked paid with the statement's amount and
// reference; a line with no expected commission is recorded as a paid adjustment with expected 0 — unknown is not
// invented). Accepting the same line twice is refused.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, created, opt, put, touched, type Ctx } from './cmd.ts';
import { normalizeDay, type Day } from './dates.ts';
import { detectCurrency, parseAmount } from './extraction.ts';
import { fromMinor, toMinor } from './money.ts';
import { policyNumberKey } from './premiums.ts';
import { searchKey } from './rules.ts';
import { policyInsurerNames } from './catalog.ts';
import type { CommissionEntry, Policy } from './schema.ts';

export const STATEMENT_FIELDS = ['policyNumber', 'amount', 'period'] as const;
export type StatementField = typeof STATEMENT_FIELDS[number];
export type StatementMapping = Partial<Record<StatementField, number>>;
const HINTS: Record<StatementField, RegExp> = {
  policyNumber: /policy|polic|συμβολ|contract/,
  amount: /commission|amount|προμηθει|ποσο|paid|καταβλ/,
  period: /period|month|περιοδ|μηνας|date|ημερομ/,
};
/** a first guess of the column mapping from the header row (the advisor confirms or changes it) */
export function guessStatementMapping(header: readonly string[]): StatementMapping {
  const out: StatementMapping = {};
  const used = new Set<number>();
  for (const f of STATEMENT_FIELDS) {
    const i = header.findIndex((h, idx) => !used.has(idx) && HINTS[f].test(searchKey(h)));
    if (i >= 0) { out[f] = i; used.add(i); }
  }
  return out;
}

const MONTHS: [RegExp, number][] = [[/^ιαν|^jan/, 1], [/^φεβ|^feb/, 2], [/^μαρ|^mar/, 3], [/^απρ|^apr/, 4], [/^μαι|^may/, 5], [/^ιουν|^jun/, 6], [/^ιουλ|^jul/, 7], [/^αυγ|^aug/, 8], [/^σεπ|^sep/, 9], [/^οκτ|^oct/, 10], [/^νοε|^nov/, 11], [/^δεκ|^dec/, 12]];
/** "2026-09", "09/2026", "9.2026", "Sep 2026", «Σεπτέμβριος 2026», or a full date → "2026-09" (null when unreadable) */
export function statementPeriod(text: string): string | null {
  const s = String(text ?? '').trim();
  if (!s) return null;
  const ym = (y: number, m: number) => (m >= 1 && m <= 12 && y >= 2000 && y <= 2100 ? `${y}-${String(m).padStart(2, '0')}` : null);
  let m = /^(\d{4})[-/.](\d{1,2})$/.exec(s);
  if (m) return ym(+m[1]!, +m[2]!);
  m = /^(\d{1,2})[-/.](\d{4})$/.exec(s);
  if (m) return ym(+m[2]!, +m[1]!);
  const d = normalizeDay(s.length >= 8 && /\d/.test(s) && /^[\d\s/.\-]+$/.test(s) ? s : '');
  if (d) return d.slice(0, 7);
  m = /^(\p{L}{3,})\.?\s+(\d{4})$/u.exec(s);
  if (m) { const w = searchKey(m[1]!); const mo = MONTHS.find(([r]) => r.test(w))?.[1]; if (mo) return ym(+m[2]!, mo); }
  return null;
}

export interface StatementLine { line: number; policyNumber: string; amountText: string; periodText: string; amount: number; period: string }
export type StatementProblem = 'no_number' | 'amount' | 'period';
export interface InvalidStatementRow { line: number; policyNumber: string; amountText: string; periodText: string; problem: StatementProblem }
export type ReconStatus = 'matched' | 'different' | 'missing' | 'unexpected';
export type UnexpectedReason = 'no_policy' | 'ambiguous' | 'no_entry';
export interface ReconItem {
  /** stable key of the policy + period group (also the idempotency key when accepted) */
  key: string; status: ReconStatus; reason?: UnexpectedReason;
  policyNumber: string; period: string; policy: Policy | null; personId: string | null;
  expected: number | null; paid: number | null; difference: number | null; currency: string;
  /** the expected lines (open or already paid) and the statement rows of this group */
  entries: CommissionEntry[]; lines: StatementLine[];
  /** already recorded from this statement (accepted earlier) */
  accepted: boolean;
}
export interface Reconciliation {
  statementRef: string; periods: string[]; items: ReconItem[]; invalid: InvalidStatementRow[];
  summary: Record<ReconStatus, { count: number; expected: number; paid: number }> & { total: { expected: number; paid: number; difference: number } };
}

const add = (a: number, b: number, cur: string) => fromMinor(toMinor(a, cur) + toMinor(b, cur), cur);

/**
 * Compare a statement with the expected commission. `rows` include the header unless `hasHeader: false`. Missing
 * lines are looked for only in the periods the statement covers (or `periods` when given). Nothing is written.
 */
export function reconcileStatement(v: WorkspaceView, rows: readonly (readonly string[])[], mapping: StatementMapping, opts: { statementRef: string; hasHeader?: boolean; periods?: string[]; currency?: string; insurer?: string | null }): Reconciliation {
  if (mapping.policyNumber == null || mapping.amount == null || mapping.period == null) throw new DomainError('commission.mapping');
  const ref = opts.statementRef.trim() || 'statement';
  const fallbackCur = (opts.currency ?? 'EUR').toUpperCase();
  const lines: StatementLine[] = [], invalid: InvalidStatementRow[] = [];
  rows.forEach((r, i) => {
    if (opts.hasHeader !== false && i === 0) return;
    const raw = { line: i + 1, policyNumber: String(r[mapping.policyNumber!] ?? '').trim(), amountText: String(r[mapping.amount!] ?? '').trim(), periodText: String(r[mapping.period!] ?? '').trim() };
    if (!raw.policyNumber && !raw.amountText && !raw.periodText) return;
    if (!policyNumberKey(raw.policyNumber)) return void invalid.push({ ...raw, problem: 'no_number' });
    const amount = raw.amountText ? parseAmount(raw.amountText) : null;
    if (amount === null) return void invalid.push({ ...raw, problem: 'amount' });
    const period = statementPeriod(raw.periodText);
    if (!period) return void invalid.push({ ...raw, problem: 'period' });
    lines.push({ ...raw, amount, period });
  });
  const periods = [...new Set(opts.periods?.length ? opts.periods : lines.map((l) => l.period))].sort();
  const byNumber = new Map<string, Policy[]>();
  for (const p of v.rows('policies')) { const k = policyNumberKey(p.policyNumber); if (k) byNumber.set(k, [...(byNumber.get(k) ?? []), p]); }
  const entriesOf = (policyId: string, period: string) => v.where('commission_entries', 'policyId', policyId).filter((e) => e.period === period && e.status !== 'cancelled');
  const items: ReconItem[] = [];
  const groups = new Map<string, StatementLine[]>();
  for (const l of lines) { const k = `${policyNumberKey(l.policyNumber)}|${l.period}`; groups.set(k, [...(groups.get(k) ?? []), l]); }
  const seenEntries = new Set<string>();
  for (const [gk, ls] of groups) {
    const first = ls[0]!;
    const pols = byNumber.get(policyNumberKey(first.policyNumber)) ?? [];
    const policy = pols.length === 1 ? pols[0]! : null;
    const entries = policy ? entriesOf(policy.id, first.period) : [];
    entries.forEach((e) => seenEntries.add(e.id));
    const currency = entries[0]?.currency ?? (policy?.currency ?? detectCurrency(ls.map((x) => x.amountText).join(' ')) ?? fallbackCur).toUpperCase();
    const paid = ls.reduce((s, x) => add(s, x.amount, currency), 0);
    const expected = entries.length ? entries.reduce((s, e) => add(s, e.expectedAmount, currency), 0) : null;
    const key = `stmt:${ref}:${gk}`;
    const accepted = entries.some((e) => e.statementRef === ref) || v.where('commission_entries', 'key', key).length > 0;
    let status: ReconStatus, reason: UnexpectedReason | undefined;
    if (pols.length === 0) { status = 'unexpected'; reason = 'no_policy'; }
    else if (pols.length > 1) { status = 'unexpected'; reason = 'ambiguous'; }
    else if (!entries.length) { status = 'unexpected'; reason = 'no_entry'; }
    else status = Math.abs(toMinor(paid, currency) - toMinor(expected!, currency)) <= 1 ? 'matched' : 'different';
    items.push({ key, status, ...(reason ? { reason } : {}), policyNumber: first.policyNumber, period: first.period, policy, personId: policy?.personId ?? null, expected, paid, difference: expected === null ? null : fromMinor(toMinor(paid, currency) - toMinor(expected, currency), currency), currency, entries, lines: ls, accepted });
  }
  // missing: open expected lines in the covered periods (for this insurer when given) with no statement line
  const insurer = opt(opts.insurer) ? searchKey(opts.insurer!) : null;
  const missing = new Map<string, CommissionEntry[]>();
  for (const e of v.rows('commission_entries')) {
    if (!periods.includes(e.period) || seenEntries.has(e.id) || (e.status !== 'expected' && e.status !== 'pending') || !e.policyId) continue;
    const p = v.get('policies', e.policyId);
    // v14: the catalogue insurer's current name counts too (a policy linked before a rename)
    if (insurer && !policyInsurerNames(v, p).some((n) => searchKey(n).includes(insurer))) continue;
    const k = `${e.policyId}|${e.period}`;
    missing.set(k, [...(missing.get(k) ?? []), e]);
  }
  for (const [, es] of missing) {
    const p = v.get('policies', es[0]!.policyId!)!;
    const currency = es[0]!.currency;
    const expected = es.reduce((s, e) => add(s, e.expectedAmount, currency), 0);
    items.push({ key: `missing:${p.id}:${es[0]!.period}`, status: 'missing', policyNumber: p.policyNumber ?? '', period: es[0]!.period, policy: p, personId: p.personId, expected, paid: null, difference: null, currency, entries: es, lines: [], accepted: false });
  }
  const order: Record<ReconStatus, number> = { different: 0, missing: 1, unexpected: 2, matched: 3 };
  items.sort((a, b) => order[a.status] - order[b.status] || a.period.localeCompare(b.period) || a.policyNumber.localeCompare(b.policyNumber));
  const empty = () => ({ count: 0, expected: 0, paid: 0 });
  const summary = { matched: empty(), different: empty(), missing: empty(), unexpected: empty(), total: { expected: 0, paid: 0, difference: 0 } };
  const r2 = (n: number) => Math.round(n * 100) / 100;
  for (const it of items) {
    const s = summary[it.status];
    s.count++; s.expected = r2(s.expected + (it.expected ?? 0)); s.paid = r2(s.paid + (it.paid ?? 0));
    summary.total.expected = r2(summary.total.expected + (it.expected ?? 0)); summary.total.paid = r2(summary.total.paid + (it.paid ?? 0));
  }
  summary.total.difference = r2(summary.total.paid - summary.total.expected);
  return { statementRef: ref, periods, items, invalid, summary };
}

/** whether a reconciliation line can be accepted (recorded) */
export const canAccept = (it: ReconItem) => !it.accepted && it.status !== 'missing' && it.lines.length > 0 && (!it.entries.length || it.entries.some((e) => e.status !== 'paid'));

/**
 * Record accepted statement lines (one changeset). Expected lines of the group are marked paid with the statement
 * amount (split over several lines in order, the last one takes the remainder), paid date and statement reference.
 * A group with no expected line is recorded as a paid 'adjustment' line with expected 0 and the statement details.
 */
export function acceptStatementLines(v: WorkspaceView, items: readonly ReconItem[], input: { statementRef: string; paidDate?: Day | null }, ctx: Ctx): Changeset {
  const ref = input.statementRef.trim() || 'statement';
  const paidDate = normalizeDay(input.paidDate ?? ctx.today) ?? ctx.today;
  const puts: Put[] = [];
  for (const it of items) {
    if (it.status === 'missing' || !it.lines.length) throw new DomainError('commission.nothing_to_accept');
    if (v.where('commission_entries', 'key', it.key).length) throw new DomainError('commission.already_accepted');
    const open = it.entries.map((e) => v.get('commission_entries', e.id)!).filter((e) => e && e.status !== 'cancelled');
    if (open.some((e) => e.statementRef === ref)) throw new DomainError('commission.already_accepted');
    const statement = { statementLines: it.lines.map((l) => ({ line: l.line, policyNumber: l.policyNumber, amount: l.amount, period: l.period })) };
    const toPay = open.filter((e) => e.status !== 'paid');
    // every expected line is already paid (by hand or another statement): nothing is recorded twice
    if (open.length && !toPay.length) throw new DomainError('commission.already_paid');
    if (toPay.length) {
      let left = toMinor(it.paid ?? 0, it.currency);
      toPay.forEach((e, i) => {
        const share = i === toPay.length - 1 ? left : Math.min(left, toMinor(e.expectedAmount, it.currency));
        left -= share;
        puts.push(put('commission_entries', touched(ctx, e, { status: 'paid', paidAmount: fromMinor(share, it.currency), paidDate, statementRef: ref, data: { ...((e.data as object | null) ?? {}), ...statement, acceptedFrom: it.status } })));
      });
    } else {
      puts.push(put('commission_entries', created<'commission_entries'>(ctx, {
        policyId: it.policy?.id ?? null, personId: it.personId, productionEventId: null, ruleId: null, kind: 'adjustment', period: it.period, ratePct: null,
        expectedAmount: 0, paidAmount: it.paid ?? 0, currency: it.currency, status: 'paid', statementRef: ref, paidDate, key: it.key,
        notes: it.policy ? null : `${it.policyNumber}`, data: { ...statement, acceptedFrom: it.status, reason: it.reason ?? null },
      })));
    }
  }
  if (!puts.length) throw new DomainError('commission.nothing_to_accept');
  return { action: 'commission.accept_statement', entity: 'commission_entries', summary: String(items.length), puts, deletes: [] };
}

/** the reconciliation as rows for CSV export / print (labels translated by the caller) */
export function reconciliationRows(r: Reconciliation, label: (status: ReconStatus, reason?: UnexpectedReason) => string, name: (personId: string | null) => string): (string | number)[][] {
  return r.items.map((it) => [it.period, it.policyNumber, name(it.personId), it.policy?.productName ?? '', label(it.status, it.reason), it.expected ?? '', it.paid ?? '', it.difference ?? '', it.currency, it.accepted ? 'x' : '']);
}
